import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PropTypes from "prop-types";

const FloatingActionButton = ({
  icon: Icon,
  onClick,
  position = "bottom-right",
  color = "primary",
  size = "large",
  tooltip,
  disabled = false,
  children,
  speedDial = false,
  speedDialActions = [],
  glassMorphism = true,
  magnetic = true,
  ripple = true,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [ripples, setRipples] = useState([]);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const buttonRef = useRef(null);
  const magneticRef = useRef(null);

  // Position classes
  const positionClasses = {
    "bottom-right": "bottom-6 right-6",
    "bottom-left": "bottom-6 left-6",
    "top-right": "top-6 right-6",
    "top-left": "top-6 left-6",
  };

  // Size classes
  const sizeClasses = {
    small: "w-10 h-10",
    medium: "w-12 h-12",
    large: "w-14 h-14",
  };

  // Color classes — always solid
  const colorClasses = {
    primary: "bg-sg-accent hover:bg-[var(--sg-accent-hover)] text-sg-accent-ink",
    secondary: "bg-gray-700 hover:bg-gray-800 text-white",
    success: "bg-green-600 hover:bg-green-700 text-white",
    danger: "bg-red-600 hover:bg-red-700 text-white",
  };

  // Handle magnetic effect
  const handleMouseMove = (e) => {
    if (!magnetic || !magneticRef.current) return;

    const rect = magneticRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    const distance = Math.sqrt(x * x + y * y);
    const maxDistance = 50;

    if (distance < maxDistance) {
      const force = (maxDistance - distance) / maxDistance;
      setMousePosition({ x: x * force * 0.3, y: y * force * 0.3 });
    }
  };

  const handleMouseLeave = () => {
    setMousePosition({ x: 0, y: 0 });
  };

  // Handle ripple effect
  const handleClick = (e) => {
    if (ripple && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const id = Date.now();

      setRipples([...ripples, { x, y, id }]);
      setTimeout(() => {
        setRipples((prev) => prev.filter((r) => r.id !== id));
      }, 1000);
    }

    if (speedDial) {
      setIsOpen(!isOpen);
    } else if (onClick) {
      onClick(e);
    }
  };

  // Close speed dial on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (buttonRef.current && !buttonRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [isOpen]);

  const glassStyles = "";

  return (
    <div className={`fixed ${positionClasses[position]} z-50`}>
      {/* Speed Dial Actions */}
      <AnimatePresence>
        {speedDial && isOpen && (
          <div className="absolute bottom-full mb-4 space-y-2">
            {speedDialActions.map((action, index) => (
              <motion.div
                key={action.id || index}
                initial={{ opacity: 0, y: 20, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.8 }}
                transition={{
                  delay: index * 0.05,
                  type: "spring",
                  stiffness: 300,
                  damping: 25,
                }}
              >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    action.onClick();
                    setIsOpen(false);
                  }}
                  className={`
                    relative flex items-center justify-center
                    ${sizeClasses[size]} ${colorClasses[action.color || color]}
                    ${glassStyles}
                    rounded-full shadow-lg hover:shadow-xl
                    transform transition-all duration-300
                    ${action.disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                  `}
                  disabled={action.disabled}
                >
                  {action.icon && <action.icon className="w-5 h-5" />}
                  {action.tooltip && (
                    <span className="absolute right-full mr-3 px-2 py-1 text-sm whitespace-nowrap bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      {action.tooltip}
                    </span>
                  )}
                </motion.button>
              </motion.div>
            ))}
          </div>
        )}
      </AnimatePresence>

      {/* Main Button */}
      <motion.div
        ref={magneticRef}
        animate={{
          x: mousePosition.x,
          y: mousePosition.y,
        }}
        transition={{ type: "spring", stiffness: 150, damping: 15 }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <motion.button
          ref={buttonRef}
          onClick={handleClick}
          disabled={disabled}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          animate={{ rotate: isOpen ? 45 : 0 }}
          className={`
            relative group flex items-center justify-center
            ${sizeClasses[size]} ${colorClasses[color]}
            ${glassStyles}
            rounded-full shadow-lg hover:shadow-xl
            transform transition-all duration-300
            ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
            overflow-hidden
          `}
          {...props}
        >
          {/* Ripple Effect */}
          {ripples.map((ripple) => (
            <motion.span
              key={ripple.id}
              className="absolute bg-white/30 rounded-full pointer-events-none"
              style={{
                left: ripple.x,
                top: ripple.y,
                width: 10,
                height: 10,
                transform: "translate(-50%, -50%)",
              }}
              initial={{ scale: 0, opacity: 1 }}
              animate={{ scale: 8, opacity: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
            />
          ))}

          {/* Icon */}
          {Icon && <Icon className="w-6 h-6 relative z-10" />}
          {children && <span className="relative z-10">{children}</span>}

          {/* Tooltip */}
          {tooltip && !speedDial && (
            <span className="absolute bottom-full mb-2 px-2 py-1 text-sm whitespace-nowrap bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
              {tooltip}
            </span>
          )}
        </motion.button>
      </motion.div>
    </div>
  );
};

FloatingActionButton.propTypes = {
  icon: PropTypes.elementType,
  onClick: PropTypes.func,
  position: PropTypes.oneOf([
    "bottom-right",
    "bottom-left",
    "top-right",
    "top-left",
  ]),
  color: PropTypes.oneOf(["primary", "secondary", "success", "danger"]),
  size: PropTypes.oneOf(["small", "medium", "large"]),
  tooltip: PropTypes.string,
  disabled: PropTypes.bool,
  children: PropTypes.node,
  speedDial: PropTypes.bool,
  speedDialActions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      icon: PropTypes.elementType,
      onClick: PropTypes.func.isRequired,
      tooltip: PropTypes.string,
      color: PropTypes.oneOf(["primary", "secondary", "success", "danger"]),
      disabled: PropTypes.bool,
    }),
  ),
  glassMorphism: PropTypes.bool,
  magnetic: PropTypes.bool,
  ripple: PropTypes.bool,
};

export default FloatingActionButton;
